import { forwardRef, KeyboardEvent } from "react";
import IconKeyboardArrowDown from "@servicetitan/hammer-icon/mdi/round/keyboard_arrow_down.svg";

import { Button } from "../../Button";
import { Flex } from "../../Flex";
import { Menu } from "../../Menu";
import { Text } from "../../Text";

import styles from "./PaginationItemsPerPageMenu.module.scss";

/**
 * Props for the PaginationItemsPerPageMenu component
 */
export type PaginationItemsPerPageMenuProps = {
  /**
   * The currently selected number of items per page.
   */
  itemsPerPage: number;
  /**
   * Array of available options for items per page.
   */
  itemsPerPageOptions: number[];
  /**
   * Callback function called when the items per page selection changes.
   * @param itemsPerPage The new number of items per page
   */
  onItemsPerPageChange: (itemsPerPage: number) => void;
  /**
   * Custom label displayed before the items-per-page selector.
   * @default "Rows per page"
   */
  itemsPerPageLabel?: string;
};

/**
 * PaginationItemsPerPageMenu component for selecting the number of items per page.
 *
 * Features:
 * - Displays current items per page selection
 * - Provides dropdown menu with available options
 * - Integrates with pagination system
 * - Shows "Rows per page" label for clarity
 * - Fully accessible with proper ARIA attributes
 * - Consistent styling with design system
 *
 * @example
 * <PaginationItemsPerPageMenu
 *   itemsPerPage={10}
 *   itemsPerPageOptions={[5, 10, 25, 50]}
 *   onItemsPerPageChange={(itemsPerPage) => console.log('Items per page:', itemsPerPage)}
 * />
 */
export const PaginationItemsPerPageMenu = forwardRef<
  HTMLDivElement,
  PaginationItemsPerPageMenuProps
>((props, ref) => {
  const {
    itemsPerPage,
    itemsPerPageOptions,
    onItemsPerPageChange,
    itemsPerPageLabel = "Rows per page",
  } = props;

  // This is a workaround to focus the first item in the menu when the arrow keys are pressed with the custom menu trigger
  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.code === "ArrowDown" || e.code === "ArrowUp") {
      e.preventDefault();
      const menuId = e.currentTarget.getAttribute("aria-controls");

      setTimeout(() => {
        const menu = menuId ? document.getElementById(menuId) : null;
        const menuItems =
          menu?.querySelectorAll('[data-anv="menu-item"]') ||
          document.querySelectorAll('[data-anv="menu-item"]');

        (menuItems[0] as HTMLElement)?.focus();
      }, 100);
    }
  };

  return (
    <Flex alignItems="center" gap="2" ref={ref}>
      <Text size="small">{itemsPerPageLabel}</Text>
      <Menu
        trigger={({ ref, ...rest }) => {
          return (
            <Button
              ref={ref}
              {...rest}
              onKeyDown={handleKeyDown}
              className={styles["pagination-items-per-page-button"]}
              appearance="ghost"
              icon={{ after: IconKeyboardArrowDown }}
              size="small"
            >
              {itemsPerPage}
            </Button>
          );
        }}
      >
        {itemsPerPageOptions.map((option) => (
          <Menu.Item
            key={option}
            label={option}
            onClick={() => {
              onItemsPerPageChange?.(option);
            }}
          />
        ))}
      </Menu>
    </Flex>
  );
});

PaginationItemsPerPageMenu.displayName = "PaginationItemsPerPageMenu";
